import { ResourceFile, ResourceType } from "../Interfaces"
import { ResourceUploader } from "./ResourceUploader"
import { ResourceList } from "./ResourceList"

// Container / Header for the resource list
export function ResourceListContainer({ 
    resources, 
    type, 
    onResourceRefresh
}: { 
    resources: ResourceFile[], 
    type: ResourceType,
    onResourceRefresh: () => void
}) {
    return (
        <PageSection>
            <ContentWrapper>
                <StatsAndControlsCard>
                    <LibraryInfoSection resources={resources} type={type} onResourceRefresh={onResourceRefresh} />
                    <FileTypeStatsSection resources={resources} />
                </StatsAndControlsCard>
                <ResourceList 
                    resources={resources} 
                    type={type} 
                    onResourceRefresh={onResourceRefresh}
                />
            </ContentWrapper>
        </PageSection>
    )
}

// Main page section with background
function PageSection({ children }: { children: React.ReactNode }) {
    return (
        <div className="flex-1 bg-gray-50/50">
            {children}
        </div>
    )
}

// Content wrapper with max width and padding
function ContentWrapper({ children }: { children: React.ReactNode }) {
    return (
        <div className="max-w-7xl mx-auto px-6 py-8">
            {children}
        </div>
    )
}

// White card container for stats and controls
function StatsAndControlsCard({ children }: { children: React.ReactNode }) {
    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
            <div className="flex items-center justify-between">
                {children}
            </div>
        </div>
    )
}

// Left section with library info and controls
function LibraryInfoSection({ resources, type, onResourceRefresh }: { resources: ResourceFile[], type: ResourceType, onResourceRefresh: () => void }) {
    return (
        <div className="flex items-center space-x-6">
            <LibraryTitleAndCount resources={resources} />
            <ControlBadges type={type} onResourceRefresh={onResourceRefresh} />
        </div>
    )
}

// Library title and resource count
function LibraryTitleAndCount({ resources }: { resources: ResourceFile[] }) {
    return (
        <div>
            <h2 className="text-2xl font-bold text-gray-900">Resource Library</h2>
            <ResourceCount count={resources.length} />
        </div>
    )
}

// Resource count with proper pluralization
function ResourceCount({ count }: { count: number }) {
    return (
        <p className="text-gray-600 mt-1">
            {count} {count === 1 ? 'resource' : 'resources'} available
        </p>
    )
}

// Control badges (Browse files, Secure storage, Upload button)
function ControlBadges({ type, onResourceRefresh }: { type: ResourceType, onResourceRefresh: () => void }) {
    return (
        <div className="hidden sm:flex items-center space-x-4 text-sm">
            <SecureStorageBadge />
            <UploadSection type={type} onResourceRefresh={onResourceRefresh} />
        </div>
    )
}

// Secure storage badge with orange styling
function SecureStorageBadge() {
    return (
        <div className="flex items-center space-x-2 bg-orange-50 px-3 py-2 rounded-lg">
            <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
            <span className="text-orange-700">Secure storage</span>
        </div>
    )
}

// Upload button section
function UploadSection({ type, onResourceRefresh }: { type: ResourceType, onResourceRefresh: () => void }) {
    return (
        <div className="bg-white border-gray-200">
            <div className="max-w-7xl mx-auto text-center">
                <ResourceUploader type={type} onResourceRefresh={onResourceRefresh} />
            </div>
        </div>
    )
}

// Right section with file type statistics
function FileTypeStatsSection({ resources }: { resources: ResourceFile[] }) {
    return (
        <div className="hidden lg:flex items-center space-x-6">
            <FileTypeStat 
                count={resources.filter(r => r.type === 'pdf').length} 
                label="PDFs" 
            />
            <FileTypeStat 
                count={resources.filter(r => r.type === 'mp4').length} 
                label="Videos" 
            />
            <FileTypeStat 
                count={resources.filter(r => ['jpg', 'png'].includes(r.type)).length} 
                label="Images" 
            />
        </div>
    )
}

// Individual file type statistic
function FileTypeStat({ count, label }: { count: number, label: string }) {
    return (
        <div className="text-center">
            <StatNumber count={count} />
            <StatLabel label={label} />
        </div>
    )
}

// Large number display for stat
function StatNumber({ count }: { count: number }) {
    return (
        <div className="text-2xl font-bold text-gray-900">{count}</div>
    )
}

// Small label for stat
function StatLabel({ label }: { label: string }) {
    return (
        <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
    )
}
